import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';
import { GoogleLogo, Eye, EyeSlash } from '@phosphor-icons/react';

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', username: '', email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error('Password minimal 6 karakter');
      return;
    }
    setLoading(true);
    try {
      await register(form);
      setIsSent(true);
      toast.success('Pendaftaran berhasil! Cek email kamu.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal mendaftar');
    } finally {
      setLoading(false);
    }
  };

  if (isSent) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="max-w-md mx-auto px-4 py-12 md:py-16 text-center">
          <div className="border-2 border-foreground bg-white shadow-brutal-lg p-10">
            <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-foreground">
              <span className="text-4xl">📧</span>
            </div>
            <h1 className="font-display text-3xl font-bold mb-4">Cek Email Kamu!</h1>
            <p className="text-foreground/70 leading-relaxed mb-8">
              Kami telah mengirimkan tautan aktivasi ke <strong>{form.email}</strong>. 
              Silakan klik tautan tersebut untuk mengaktifkan akunmu.
            </p>
            <div className="bg-accent/10 border-2 border-dashed border-foreground/30 p-4 mb-8 text-sm">
              <p className="font-bold text-primary mb-1">💡 Belum menerima email?</p>
              <p>Coba cek folder <strong>SPAM</strong> atau tunggu beberapa menit ya.</p>
            </div>
            <Link to="/login" className="font-bold underline underline-offset-4">
              Kembali ke Login
            </Link>
          </div>
        </main>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-md mx-auto px-4 py-12 md:py-16">
        <div className="border-2 border-foreground bg-white shadow-brutal-lg p-8" data-testid="register-card">
          <p className="text-xs uppercase tracking-[0.3em] font-bold mb-2">Daftar Gratis</p>
          <h1 className="font-display text-4xl font-bold tracking-tight mb-6" data-testid="register-title">
            Mulai Terima <span className="bg-primary text-white px-1">Traktiran.</span>
          </h1>


          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest font-bold block mb-2">Nama Tampil</label>
              <input name="name" required value={form.name} onChange={onChange}
                className="w-full border-2 border-foreground bg-white px-4 py-3 shadow-brutal-sm focus:outline-none focus:border-primary focus:shadow-none focus:translate-x-[2px] focus:translate-y-[2px] transition-all"
                placeholder="Nadia Rizqi"
                data-testid="register-name-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold block mb-2">Username</label>
              <div className="flex border-2 border-foreground bg-white shadow-brutal-sm focus-within:shadow-none focus-within:translate-x-[2px] focus-within:translate-y-[2px] transition-all">
                <span className="inline-flex items-center border-r-2 border-foreground bg-secondary/30 px-3 text-foreground/60 text-xs font-bold whitespace-nowrap">
                  traktirkan/u/
                </span>
                <input 
                  name="username" 
                  required 
                  value={form.username} 
                  onChange={onChange}
                  className="flex-1 bg-transparent px-4 py-3 focus:outline-none min-w-0"
                  placeholder="username"
                  data-testid="register-username-input" 
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold block mb-2">Email</label>
              <input name="email" type="email" required value={form.email} onChange={onChange}
                className="w-full border-2 border-foreground bg-white px-4 py-3 shadow-brutal-sm focus:outline-none focus:border-primary focus:shadow-none focus:translate-x-[2px] focus:translate-y-[2px] transition-all"
                placeholder="kamu@email.com"
                data-testid="register-email-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold block mb-2">Password</label>
              <div className="relative">
                <input name="password"
                  type={showPassword ? "text" : "password"}
                  required minLength={6} value={form.password} onChange={onChange}
                  className="w-full border-2 border-foreground bg-white px-4 pr-12 py-3 shadow-brutal-sm focus:outline-none focus:border-primary focus:shadow-none focus:translate-x-[2px] focus:translate-y-[2px] transition-all"
                  placeholder="Minimal 6 karakter"
                  data-testid="register-password-input" />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground/50 hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeSlash size={20} weight="bold" /> : <Eye size={20} weight="bold" />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full border-2 border-foreground bg-primary text-white px-4 py-3 font-bold uppercase tracking-wide shadow-brutal hover:shadow-brutal-sm hover:translate-x-[2px] hover:translate-y-[2px] active:shadow-none active:translate-x-[4px] active:translate-y-[4px] transition-all disabled:opacity-60"
              data-testid="register-submit-btn"
            >
              {loading ? 'Mendaftar...' : 'Daftar Sekarang'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm">
            Udah punya akun?{' '}
            <Link to="/login" className="font-bold underline underline-offset-4" data-testid="register-login-link">
              Masuk di sini
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}
